import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, arrayUnion, collection, getDocs } from 'firebase/firestore';
import { formatDisplayDate, getCurrentTimestamp } from '../utils/dateUtils';
import { TABLE_ICONS } from '../utils/tableIcons';
import Snackbar from '../components/Snackbar';
import { useSnackbar } from '../hooks/useSnackbar';
import './LeaderMemberDetails.css';

const LeaderMemberDetails = () => {
  const { leaderId } = useParams();
  const navigate = useNavigate();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  
  const [leader, setLeader] = useState(null);
  const [members, setMembers] = useState([]);
  const [allLeaders, setAllLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [showDeleteMemberConfirm, setShowDeleteMemberConfirm] = useState(false);
  
  const [newMember, setNewMember] = useState({
    name: '',
    mobileNumber: '',
    address: ''
  });
  const [memberError, setMemberError] = useState('');
  const [memberLoading, setMemberLoading] = useState(false);
  
  const [editingMember, setEditingMember] = useState(null);
  const [editMemberData, setEditMemberData] = useState({ name: '', address: '' });
  const [deletingMemberPhone, setDeletingMemberPhone] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAllLeaders = useCallback(async () => {
    try {
      const groupsSnapshot = await getDocs(collection(db, 'myleaders'));
      const leadersData = [];
      
      groupsSnapshot.forEach((doc) => {
        const data = doc.data();
        leadersData.push({
          id: doc.id,
          name: data.name || 'Unknown Leader',
          memberList: data.memberList || []
        });
      });
      
      setAllLeaders(leadersData);
      
    } catch (error) {
      console.error('Error fetching all leaders:', error);
    }
  }, []);

  const fetchLeaderDetails = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch leader data
      const leaderDoc = await getDoc(doc(db, 'myleaders', leaderId));
      if (!leaderDoc.exists()) {
        showSnackbar('Leader not found', 'error');
        navigate('/admin/leaders');
        return;
      }
      
      const leaderData = leaderDoc.data();
      const leaderInfo = {
        id: leaderDoc.id,
        name: leaderData.name || 'Unknown Leader',
        memberCount: leaderData.memberList ? leaderData.memberList.length : 0,
        memberList: leaderData.memberList || [],
        visibilityEnabled: leaderData.visibilityEnabled !== undefined ? leaderData.visibilityEnabled : false
      };
      
      setLeader(leaderInfo);
      
      // Fetch member details
      const memberDetails = [];
      for (const memberPhone of leaderInfo.memberList) {
        try {
          const phoneStr = memberPhone.toString();
          const userDoc = await getDoc(doc(db, 'users', phoneStr));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            memberDetails.push({
              name: userData.name || 'Unknown',
              mobileNumber: phoneStr,
              address: userData.address || 'Not specified',
              birthday: formatDisplayDate(userData.birthday) || 'Not specified',
              pincode: userData.pincode || 'Not specified',
              email: userData.email || 'Not specified',
              businessName: userData.businessName || 'Not specified',
              businessType: userData.businessType || 'Not specified',
              city: userData.city || 'Not specified',
              state: userData.state || 'Not specified'
            });
          } else {
            memberDetails.push({
              name: 'User not found',
              mobileNumber: phoneStr,
              address: 'Not available',
              birthday: 'Not available',
              pincode: 'Not available',
              email: 'Not available',
              businessName: 'Not available',
              businessType: 'Not available',
              city: 'Not available',
              state: 'Not available'
            });
          }
        } catch (error) {
          console.error(`Error fetching user ${memberPhone}:`, error);
          memberDetails.push({
            name: 'Error loading',
            mobileNumber: memberPhone.toString(),
            address: 'Error loading',
            birthday: 'Error loading',
            pincode: 'Error loading',
            email: 'Error loading',
            businessName: 'Error loading',
            businessType: 'Error loading',
            city: 'Error loading',
            state: 'Error loading'
          });
        }
      }
      
      setMembers(memberDetails);
    } catch (error) {
      console.error('Error fetching leader details:', error);
      showSnackbar('Error loading leader details', 'error');
    } finally {
      setLoading(false);
    }
  }, [leaderId, navigate, showSnackbar]);

  useEffect(() => {
    if (leaderId) {
      fetchAllLeaders();
      fetchLeaderDetails();
    }
  }, [leaderId, fetchAllLeaders, fetchLeaderDetails]);

  // Function to check if mobile number exists in any leader group
  const checkMemberExistsInAnyLeader = (mobileNumber) => {
    try {
      if (!mobileNumber || !allLeaders || allLeaders.length === 0) {
        return { exists: false };
      }

      const phoneNumber = parseInt(mobileNumber);
      const phoneString = mobileNumber.toString();

      for (const leaderGroup of allLeaders) {
        if (!leaderGroup || !leaderGroup.memberList) {
          continue;
        }

        // Check both integer and string formats to handle data type inconsistencies
        const memberExists = leaderGroup.memberList.some(member => {
          try {
            const memberInt = parseInt(member);
            const memberStr = member.toString();
            return memberInt === phoneNumber || memberStr === phoneString || member === phoneNumber || member === phoneString;
          } catch (error) {
            console.error('Error checking member:', member, error);
            return false;
          }
        });
        
        if (memberExists) {
          
          return {
            exists: true,
            leaderName: leaderGroup.name,
            leaderId: leaderGroup.id,
            isCurrentLeader: leaderGroup.id === leaderId
          };
        }
      }

      return { exists: false };
    } catch (error) {
      console.error('Error in checkMemberExistsInAnyLeader:', error);
      return { exists: false };
    }
  };

  const handleMobileNumberChange = async (mobileNumber) => {
    setNewMember({...newMember, mobileNumber});
    setMemberError('');
    
    if (mobileNumber.trim().length >= 10) {
      // Validate mobile number format first
      if (!/^\d{10}$/.test(mobileNumber.trim())) {
        setNewMember({
          name: '',
          mobileNumber: mobileNumber,
          address: ''
        });
        setMemberError('Please enter a valid 10-digit mobile number');
        return;
      }
      
      // Check if member exists in any leader group
      const memberCheck = checkMemberExistsInAnyLeader(mobileNumber);
      if (memberCheck.exists) {
        setNewMember({
          name: '',
          mobileNumber: mobileNumber,
          address: ''
        });
        if (memberCheck.isCurrentLeader) {
          setMemberError('This member is already in this leader group');
        } else {
          setMemberError(`This member is already added to "${memberCheck.leaderName}" leader group. A member cannot be in multiple leader groups.`);
        }
        return;
      }
      
      try {
        setMemberLoading(true);
        const userDoc = await getDoc(doc(db, 'users', mobileNumber));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setNewMember({
            name: userData.name || '',
            mobileNumber: mobileNumber,
            address: userData.address || ''
          });
        } else {
          setNewMember({
            name: '',
            mobileNumber: mobileNumber,
            address: ''
          });
          setMemberError('User with this mobile number does not exist');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setMemberError('Error validating mobile number');
      } finally {
        setMemberLoading(false);
      }
    } else {
      setNewMember({
        name: '',
        mobileNumber: mobileNumber,
        address: ''
      });
    }
  };

  const handleAddMember = async () => {
    try {
      setMemberError('');
      
      if (!newMember.mobileNumber.trim()) {
        setMemberError('Mobile number is required');
        return;
      }
      
      // Validate mobile number format (basic validation)
      if (!/^\d{10}$/.test(newMember.mobileNumber.trim())) {
        setMemberError('Please enter a valid 10-digit mobile number');
        return;
      }
      
      // Check if user exists in users collection
      const userDoc = await getDoc(doc(db, 'users', newMember.mobileNumber));
      if (!userDoc.exists()) {
        setMemberError('User with this mobile number does not exist');
        return;
      }
      
      // Check if member exists in any leader group (including current one)
      const memberCheck = checkMemberExistsInAnyLeader(newMember.mobileNumber);
      if (memberCheck.exists) {
        if (memberCheck.isCurrentLeader) {
          setMemberError('This member is already in this leader group');
        } else {
          setMemberError(`This member is already added to "${memberCheck.leaderName}" leader group. A member cannot be in multiple leader groups.`);
        }
        return;
      }
      
      const userData = userDoc.data();
      
      // Update the group's member list
      const leaderRef = doc(db, 'myleaders', leaderId);
      await updateDoc(leaderRef, {
        memberList: arrayUnion(parseInt(newMember.mobileNumber)),
        updatedAt: getCurrentTimestamp()
      });
      
      // Add to current members list
      const newMemberData = {
        name: userData.name || 'Unknown',
        mobileNumber: newMember.mobileNumber,
        address: userData.address || 'Not specified',
        birthday: formatDisplayDate(userData.birthday) || 'Not specified',
        pincode: userData.pincode || 'Not specified',
        email: userData.email || 'Not specified',
        businessName: userData.businessName || 'Not specified',
        businessType: userData.businessType || 'Not specified',
        city: userData.city || 'Not specified',
        state: userData.state || 'Not specified'
      };
      
      setMembers([...members, newMemberData]);
      
      // Update leader state
      setLeader({
        ...leader,
        memberList: [...leader.memberList, parseInt(newMember.mobileNumber)],
        memberCount: leader.memberCount + 1
      });
      
      // Reset form and close modal
      setNewMember({ name: '', mobileNumber: '', address: '' });
      setMemberError('');
      setMemberLoading(false);
      setShowAddMemberModal(false);
      showSnackbar('Member added successfully!', 'success');
      
    } catch (error) {
      console.error('Error adding member:', error);
      setMemberError('Error adding member. Please try again.');
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setEditMemberData({
      name: member.name,
      address: member.address
    });
    setShowEditMemberModal(true);
  };

  const handleEditMemberSave = async () => {
    try {
      if (!editingMember || !editMemberData.name.trim()) {
        showSnackbar('Member name cannot be empty', 'warning');
        return;
      }
      
      // Update user data in Firebase
      const userRef = doc(db, 'users', editingMember.mobileNumber);
      await updateDoc(userRef, {
        name: editMemberData.name.trim(),
        address: editMemberData.address.trim(),
        updatedAt: getCurrentTimestamp()
      });
      
      // Update local members state
      setMembers(members.map(member => 
        member.mobileNumber === editingMember.mobileNumber
          ? {
              ...member,
              name: editMemberData.name.trim(),
              address: editMemberData.address.trim()
            }
          : member
      ));
      
      setShowEditMemberModal(false);
      setEditingMember(null);
      setEditMemberData({ name: '', address: '' });
      showSnackbar('Member updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating member:', error);
      showSnackbar('Failed to update member. Please try again.', 'error');
    }
  };

  const handleDeleteMember = (memberPhone) => {
    setDeletingMemberPhone(memberPhone);
    setShowDeleteMemberConfirm(true);
  };

  const handleDeleteMemberConfirm = async () => {
    try {
      if (!deletingMemberPhone || !leader) return;
      
      // Remove member from the group's memberList in Firebase
      const leaderRef = doc(db, 'myleaders', leaderId);
      const updatedMemberList = leader.memberList.filter(
        phone => phone.toString() !== deletingMemberPhone.toString()
      );
      
      await updateDoc(leaderRef, {
        memberList: updatedMemberList,
        updatedAt: getCurrentTimestamp()
      });
      
      // Update local state
      setMembers(members.filter(member => member.mobileNumber !== deletingMemberPhone));
      
      // Update leader state
      setLeader({
        ...leader,
        memberList: updatedMemberList,
        memberCount: updatedMemberList.length
      });
      
      setShowDeleteMemberConfirm(false);
      setDeletingMemberPhone(null);
      showSnackbar('Member removed successfully!', 'success');
    } catch (error) {
      console.error('Error deleting member:', error);
      showSnackbar('Failed to remove member. Please try again.', 'error');
    }
  };

  // Filter members based on search term
  const filteredMembers = members.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.mobileNumber.includes(searchTerm) ||
    member.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="leader-member-details-container">
        <div className="loading">Loading leader details...</div>
      </div>
    );
  }

  if (!leader) {
    return (
      <div className="leader-member-details-container">
        <div className="error">Leader not found</div>
      </div>
    );
  }

  return (
    <div className="leader-member-details-page">
      <div className="page-header leader-member-details-header">
        <div className="header-left">
          <button 
            className="btn btn-secondary back-btn"
            onClick={() => navigate('/admin/leaders')}
          >
            ← Back to Leaders
          </button>
          <div className="leader-info">
            <h2>Members of {leader.name}</h2>
            <p className="member-count">{leader.memberCount} members</p>
          </div>
        </div>
        <div className="leader-member-details-header-actions">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddMemberModal(true)}
          >
            + Add Member
          </button>
        </div>
      </div>

      <div className="leader-member-details-table-container">
        <table className="leader-member-details-table">
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Name</th>
              <th>Mobile Number</th>
              <th>Address</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-members">
                  {searchTerm ? 'No members found matching your search.' : 'No members found. Add members to get started.'}
                </td>
              </tr>
            ) : (
              filteredMembers.map((member, index) => (
                <tr key={member.mobileNumber}>
                  <td>{index + 1}</td>
                  <td>
                    <div className="user-info">
                      <span className="user-name">{member.name}</span>
                    </div>
                  </td>
                  <td>{member.mobileNumber || '-'}</td>
                  <td>{member.address || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-icon"
                        onClick={() => handleEditMember(member)}
                        title="Edit Member"
                      >
                        {TABLE_ICONS.EDIT}
                      </button>
                      <button 
                        className="btn-icon"
                        onClick={() => handleDeleteMember(member.mobileNumber)}
                        title="Remove Member"
                      >
                        {TABLE_ICONS.DELETE}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Member</h3>
              <button
                type="button"
                className="close-btn"
                onClick={() => {
                  setShowAddMemberModal(false);
                  setNewMember({ name: '', mobileNumber: '', address: '' });
                  setMemberError('');
                  setMemberLoading(false);
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Mobile Number</label>
                <input
                  type="tel"
                  placeholder="Enter mobile number"
                  value={newMember.mobileNumber}
                  onChange={(e) => handleMobileNumberChange(e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label>Member Name</label>
                <input
                  type="text"
                  placeholder="Member Name"
                  value={newMember.name}
                  onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                  disabled
                />
              </div>
              
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  placeholder="Address"
                  value={newMember.address}
                  onChange={(e) => setNewMember({...newMember, address: e.target.value})}
                  disabled
                />
              </div>
              
              {memberLoading && (
                <div className="loading-message">Loading user data...</div>
              )}
              
              {memberError && (
                <div className="error-message">{memberError}</div>
              )}
            </div>
            
            <div className="form-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddMemberModal(false);
                  setNewMember({ name: '', mobileNumber: '', address: '' });
                  setMemberError('');
                  setMemberLoading(false);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleAddMember}
                disabled={memberLoading || !newMember.mobileNumber.trim()}
              >
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {showEditMemberModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Member</h3>
              <button
                type="button"
                className="close-btn"
                onClick={() => {
                  setShowEditMemberModal(false);
                  setEditingMember(null);
                  setEditMemberData({ name: '', address: '' });
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Member Name</label>
                <input
                  type="text"
                  placeholder="Member Name"
                  value={editMemberData.name}
                  onChange={(e) => setEditMemberData({...editMemberData, name: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  placeholder="Address"
                  value={editMemberData.address}
                  onChange={(e) => setEditMemberData({...editMemberData, address: e.target.value})}
                />
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowEditMemberModal(false);
                  setEditingMember(null);
                  setEditMemberData({ name: '', address: '' });
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleEditMemberSave}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Member Confirmation Modal */}
      {showDeleteMemberConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Confirm Remove Member</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowDeleteMemberConfirm(false);
                  setDeletingMemberPhone(null);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="warning-icon">
                ⚠️
              </div>
              
              <div className="delete-message">
                <p>Are you sure you want to remove this member from the group?</p>
                <p className="warning-text">This action cannot be undone.</p>
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteMemberConfirm(false);
                  setDeletingMemberPhone(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleDeleteMemberConfirm}
              >
                Remove Member
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.show && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={hideSnackbar}
        />
      )}
    </div>
  );
};

export default LeaderMemberDetails;
